import React, { Fragment, useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Stack,
  Button,
  TablePagination,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Warning as WarningIcon,
} from '@mui/icons-material';
import ReactECharts from 'echarts-for-react';
import { CONFIG } from '../../config/config';

interface RAIDsTabProps {
  parentEntityId: number;
  childCategory: string;
  isLeafEntity?: boolean;
  /** From dashboard hierarchy; PROGRAM/ACCOUNT use project roll-up breakdown */
  entityTypeCategory?: string | null;
}

interface RaidDimensionSummary {
  review_entity_dimension: string;
  open_count: number;
  overdue_count: number;
}

interface RaidProjectSummary {
  entity_id: number;
  entity_name: string;
  open_count: number;
  overdue_count: number;
}

interface OpenRaidBreakdownItem {
  raid_id: number;
  title: string;
  raid_category: string;
  review_entity_dimension: string;
  days_open: number;
  is_overdue: boolean;
  overdue_field?: string | null;
  project_entity_id?: number;
  project_entity_name?: string;
}

interface OpenRaidBreakdownResponse {
  group_by: 'dimension' | 'project';
  entity_id?: number;
  parent_entity_id?: number;
  total_open: number;
  total_overdue: number;
  page: number;
  page_size: number;
  total_items: number;
  truncated: boolean;
  fetch_full_list: boolean;
  dimension_summary: RaidDimensionSummary[];
  /** Always an array after normalize (empty when group_by is not project). */
  project_summary: RaidProjectSummary[];
  items: OpenRaidBreakdownItem[];
}

/** Handles older/partial API payloads so UI never reads `.length` on undefined. */
function normalizeOpenRaidBreakdown(raw: Partial<OpenRaidBreakdownResponse>): OpenRaidBreakdownResponse {
  const group_by = raw.group_by === 'project' ? 'project' : 'dimension';
  return {
    group_by,
    entity_id: raw.entity_id,
    parent_entity_id: raw.parent_entity_id,
    total_open: raw.total_open ?? 0,
    total_overdue: raw.total_overdue ?? 0,
    page: raw.page ?? 1,
    page_size: raw.page_size ?? 0,
    total_items: raw.total_items ?? 0,
    truncated: Boolean(raw.truncated),
    fetch_full_list: Boolean(raw.fetch_full_list),
    dimension_summary: Array.isArray(raw.dimension_summary) ? raw.dimension_summary : [],
    project_summary: Array.isArray(raw.project_summary) ? raw.project_summary : [],
    items: Array.isArray(raw.items) ? raw.items : [],
  };
}

interface RAIDSummaryItem {
  raid_category: string;
  total_count: number;
  open_count: number;
  in_progress_count: number;
}

interface RAIDByEntityType {
  [category: string]: Array<{ entity_type: string; count: number }>;
}

interface RAIDBySeverity {
  [category: string]: Array<{ severity: string; count: number }>;
}

interface RaidProjectRollupOverall {
  open_count: number;
  in_progress_count: number;
  overdue_count: number;
  unassigned_open_count: number;
  by_category: Record<
    string,
    { open_count: number; in_progress_count: number; overdue_count: number }
  >;
}

interface RaidProjectRollupProject {
  entity_id: number;
  entity_name: string;
  open_count: number;
  in_progress_count: number;
  overdue_count: number;
}

interface RaidAssigneeSummary {
  assignee: string;
  open_count: number;
  overdue_count: number;
}

interface RaidProjectRollupResponse {
  parent_entity_id: number;
  project_count: number;
  overall: RaidProjectRollupOverall;
  project_summary: RaidProjectRollupProject[];
  assignee_summary: RaidAssigneeSummary[];
  dimension_summary: RaidDimensionSummary[];
}

function usesProjectRollup(
  isLeafEntity: boolean,
  entityTypeCategory?: string | null
): boolean {
  return (
    !isLeafEntity &&
    Boolean(entityTypeCategory) &&
    entityTypeCategory !== 'PROJECT'
  );
}

const RAID_COLORS: Record<string, string> = {
  RISK: '#f44336',
  ASSUMPTION: '#ff9800',
  ISSUE: '#e91e63',
  DEPENDENCY: '#9c27b0',
  ACTION: '#2196f3',
  OPPORTUNITY: '#4caf50',
  NCR: '#ff5722',
  OBSERVATION: '#607d8b',
};

/** Pie slice color for RAID severity; gray when missing or not recognized. */
const SEVERITY_PIE_COLOR_UNSPECIFIED = '#9e9e9e';

function pieColorForRaidSeverity(label: string): string {
  const u = String(label ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, ' ');
  if (
    !u ||
    u === 'NOT SPECIFIED' ||
    u === 'UNSPECIFIED' ||
    u === 'N/A' ||
    u === 'UNKNOWN'
  ) {
    return SEVERITY_PIE_COLOR_UNSPECIFIED;
  }
  switch (u) {
    case 'LOW':
      return '#2e7d32';
    case 'MEDIUM':
      return '#ef6c00';
    case 'HIGH':
      return '#c62828';
    case 'CRITICAL':
      return '#6a1b9a';
    default:
      return SEVERITY_PIE_COLOR_UNSPECIFIED;
  }
}

const OPEN_RAID_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

const DIMENSION_SUMMARY_PALETTE = [
  '#1976d2',
  '#388e3c',
  '#f57c00',
  '#7b1fa2',
  '#c62828',
  '#0097a7',
  '#5d4037',
  '#455a64',
  '#6a1b9a',
  '#2e7d32',
];

function getDimensionSummaryPieOption(summary: RaidDimensionSummary[]) {
  const data = summary.map((d, i) => ({
    name: d.review_entity_dimension,
    value: d.open_count,
    itemStyle: { color: DIMENSION_SUMMARY_PALETTE[i % DIMENSION_SUMMARY_PALETTE.length] },
  }));
  return {
    title: {
      text: 'OPEN by review dimension',
      left: 'center',
      textStyle: { fontSize: 14 },
    },
    tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
    legend: { bottom: 0, type: 'scroll' },
    series: [
      {
        type: 'pie',
        radius: ['38%', '65%'],
        avoidLabelOverlap: true,
        itemStyle: { borderRadius: 4, borderColor: '#fff', borderWidth: 2 },
        label: { show: false },
        emphasis: {
          label: { show: true, fontSize: 12, fontWeight: 'bold' },
        },
        data,
      },
    ],
  };
}

function getDimensionSummaryBarOption(summary: RaidDimensionSummary[]) {
  const labels = summary.map((d) => d.review_entity_dimension);
  return {
    title: {
      text: 'OPEN and overdue by review dimension',
      left: 'center',
      textStyle: { fontSize: 14 },
    },
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    legend: { bottom: 0, data: ['Open', 'Overdue'] },
    grid: { left: '3%', right: '4%', bottom: '14%', containLabel: true },
    xAxis: {
      type: 'category',
      data: labels,
      axisLabel: { rotate: 32, interval: 0 },
    },
    yAxis: { type: 'value' },
    series: [
      {
        name: 'Open',
        type: 'bar',
        data: summary.map((d) => d.open_count),
        itemStyle: { color: '#1976d2' },
      },
      {
        name: 'Overdue',
        type: 'bar',
        data: summary.map((d) => d.overdue_count),
        itemStyle: { color: '#d32f2f' },
      },
    ],
  };
}

const RAIDsTab: React.FC<RAIDsTabProps> = ({
  parentEntityId,
  childCategory,
  isLeafEntity = false,
  entityTypeCategory = null,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [raidSummary, setRaidSummary] = useState<RAIDSummaryItem[]>([]);
  const [raidByEntityType, setRaidByEntityType] = useState<RAIDByEntityType>({});
  const [raidBySeverity, setRaidBySeverity] = useState<RAIDBySeverity>({});

  const [openBreakdown, setOpenBreakdown] = useState<OpenRaidBreakdownResponse | null>(null);
  const [openBreakdownLoading, setOpenBreakdownLoading] = useState(false);
  const [openBreakdownError, setOpenBreakdownError] = useState<string | null>(null);
  const [breakdownPage, setBreakdownPage] = useState(0);
  const [breakdownRowsPerPage, setBreakdownRowsPerPage] = useState(10);
  const [breakdownFetchFull, setBreakdownFetchFull] = useState(false);
  const [dimensionSummaryViz, setDimensionSummaryViz] = useState<'pie' | 'bar'>('pie');

  const [raidRollup, setRaidRollup] = useState<RaidProjectRollupResponse | null>(null);
  const [raidRollupLoading, setRaidRollupLoading] = useState(false);
  const [raidRollupError, setRaidRollupError] = useState<string | null>(null);

  const showProjectRollup = usesProjectRollup(isLeafEntity, entityTypeCategory);

  useEffect(() => {
    setBreakdownPage(0);
    setBreakdownFetchFull(false);
  }, [parentEntityId, entityTypeCategory]);

  useEffect(() => {
    if (!showProjectRollup || !parentEntityId) {
      setRaidRollup(null);
      setRaidRollupError(null);
      return;
    }
    const fetchRollup = async () => {
      setRaidRollupLoading(true);
      setRaidRollupError(null);
      try {
        const res = await fetch(
          `${CONFIG.SERVER_URL}/dashboard/raid/project-rollup?parent_entity_id=${parentEntityId}`
        );
        if (!res.ok) throw new Error('Failed to fetch RAID project roll-up');
        setRaidRollup((await res.json()) as RaidProjectRollupResponse);
      } catch (err) {
        setRaidRollupError(
          err instanceof Error ? err.message : 'Failed to load RAID roll-up'
        );
        setRaidRollup(null);
      } finally {
        setRaidRollupLoading(false);
      }
    };
    fetchRollup();
  }, [parentEntityId, showProjectRollup]);

  useEffect(() => {
    if (!parentEntityId) {
      setOpenBreakdown(null);
      return;
    }
    const useParent = showProjectRollup;
    const search = new URLSearchParams();
    if (useParent) {
      search.set('parent_entity_id', String(parentEntityId));
    } else {
      search.set('entity_id', String(parentEntityId));
    }
    if (breakdownFetchFull) {
      search.set('fetch_full_list', 'true');
    } else {
      search.set('page', String(breakdownPage + 1));
      search.set('page_size', String(breakdownRowsPerPage));
    }
    const fetchOpenBreakdown = async () => {
      setOpenBreakdownLoading(true);
      setOpenBreakdownError(null);
      try {
        const res = await fetch(
          `${CONFIG.SERVER_URL}/dashboard/raid/open-breakdown?${search.toString()}`
        );
        if (!res.ok) throw new Error('Failed to fetch OPEN RAID breakdown');
        const raw = (await res.json()) as Partial<OpenRaidBreakdownResponse>;
        setOpenBreakdown(normalizeOpenRaidBreakdown(raw));
      } catch (err) {
        setOpenBreakdownError(
          err instanceof Error ? err.message : 'Failed to load OPEN RAID breakdown'
        );
        setOpenBreakdown(null);
      } finally {
        setOpenBreakdownLoading(false);
      }
    };
    fetchOpenBreakdown();
  }, [
    parentEntityId,
    entityTypeCategory,
    breakdownPage,
    breakdownRowsPerPage,
    breakdownFetchFull,
  ]);

  // Fetch RAID data
  useEffect(() => {
    if (isLeafEntity) {
      const fetchEntityRAIDs = async () => {
        setLoading(true);
        setError(null);
        try {
          const raidResponse = await fetch(
            `${CONFIG.SERVER_URL}/dashboard/raid/entity?entity_id=${parentEntityId}`
          );

          if (!raidResponse.ok) throw new Error('Failed to fetch RAID data');

          const raidData = await raidResponse.json();

          setRaidSummary(raidData.summary || []);
          setRaidByEntityType({});
          setRaidBySeverity(raidData.by_severity || {});
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to fetch data');
        } finally {
          setLoading(false);
        }
      };
      fetchEntityRAIDs();
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const raidResponse = await fetch(
          `${CONFIG.SERVER_URL}/dashboard/raid/dashboard?parent_entity_id=${parentEntityId}&child_category=${encodeURIComponent(childCategory)}`
        );

        if (!raidResponse.ok) throw new Error('Failed to fetch RAID data');

        const raidData = await raidResponse.json();

        setRaidSummary(raidData.summary || []);
        setRaidByEntityType(raidData.by_entity_type || {});
        setRaidBySeverity(raidData.by_severity || {});
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    if (parentEntityId && (childCategory || isLeafEntity)) {
      fetchData();
    }
  }, [parentEntityId, childCategory, isLeafEntity]);

  // Calculate total open RAIDs
  const totalOpenRAIDs = raidSummary.reduce((sum, item) => sum + item.open_count, 0);
  const totalInProgressRAIDs = raidSummary.reduce((sum, item) => sum + item.in_progress_count, 0);

  const getRAIDBarOption = () => {
    const categories = raidSummary.map(item => item.raid_category);
    const openData = raidSummary.map(item => item.open_count);
    const inProgressData = raidSummary.map(item => item.in_progress_count);

    return {
      title: {
        text: 'Open RAIDs by Category',
        left: 'center',
        textStyle: { fontSize: 14 },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
      },
      legend: {
        bottom: 0,
        data: ['Open', 'In Progress'],
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisLabel: {
          rotate: 45,
          interval: 0,
        },
      },
      yAxis: {
        type: 'value',
      },
      series: [
        {
          name: 'Open',
          type: 'bar',
          stack: 'total',
          data: openData,
          itemStyle: { color: '#f44336' },
        },
        {
          name: 'In Progress',
          type: 'bar',
          stack: 'total',
          data: inProgressData,
          itemStyle: { color: '#ff9800' },
        },
      ],
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        containLabel: true,
      },
    };
  };

  const getRAIDPieOption = () => {
    const chartData = raidSummary.map(item => ({
      name: item.raid_category,
      value: item.open_count + item.in_progress_count,
      itemStyle: { color: RAID_COLORS[item.raid_category] || '#9e9e9e' },
    }));

    return {
      title: {
        text: 'RAIDs Distribution',
        left: 'center',
        textStyle: { fontSize: 14 },
      },
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)',
      },
      legend: {
        bottom: 0,
        type: 'scroll',
      },
      series: [
        {
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 4,
            borderColor: '#fff',
            borderWidth: 2,
          },
          label: {
            show: false,
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 12,
              fontWeight: 'bold',
            },
          },
          data: chartData,
        },
      ],
    };
  };

  const getRAIDByEntityTypeOption = (category: string, data: Array<{ entity_type: string; count: number }>) => {
    return {
      title: {
        text: `${category} by Entity Type`,
        left: 'center',
        textStyle: { fontSize: 12 },
      },
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c}',
      },
      series: [
        {
          type: 'pie',
          radius: '70%',
          data: data.map(item => ({
            name: item.entity_type,
            value: item.count,
          })),
          itemStyle: {
            borderRadius: 4,
            borderColor: '#fff',
            borderWidth: 2,
          },
          label: {
            show: false,
          },
        },
      ],
    };
  };

  const getRAIDBySeverityOption = (category: string, data: Array<{ severity: string; count: number }>) => {
    return {
      title: {
        text: `${category} by Severity`,
        left: 'center',
        textStyle: { fontSize: 12 },
      },
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c}',
      },
      series: [
        {
          type: 'pie',
          radius: '70%',
          data: data.map(item => ({
            name: item.severity,
            value: item.count,
            itemStyle: { color: pieColorForRaidSeverity(item.severity) },
          })),
          itemStyle: {
            borderRadius: 4,
            borderColor: '#fff',
            borderWidth: 2,
          },
          label: {
            show: false,
          },
        },
      ],
    };
  };

  const isProgramAccountRollup = showProjectRollup;
  const rollupOverall = raidRollup?.overall;

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Grid container spacing={2}>
      {loading ? (
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        </Grid>
      ) : (
        <Fragment>
          {showProjectRollup && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2, mb: 1 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  RAID roll-up across {raidRollup?.project_count ?? '…'} projects
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  OPEN and IN PROGRESS items on all descendant projects. Overdue applies to OPEN
                  items using the same date rules as review submit/close validation.
                </Typography>
                {raidRollupLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={28} />
                  </Box>
                )}
                {raidRollupError && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {raidRollupError}
                  </Alert>
                )}
                {!raidRollupLoading && !raidRollupError && rollupOverall && (
                  <>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={6} sm={4} md={3}>
                        <Card variant="outlined">
                          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" color="text.secondary">
                              Open (all projects)
                            </Typography>
                            <Typography variant="h5" color="error.main">
                              {rollupOverall.open_count}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={6} sm={4} md={3}>
                        <Card variant="outlined">
                          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" color="text.secondary">
                              Overdue OPEN
                            </Typography>
                            <Typography
                              variant="h5"
                              color={rollupOverall.overdue_count > 0 ? 'error.main' : 'text.primary'}
                            >
                              {rollupOverall.overdue_count}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={6} sm={4} md={3}>
                        <Card variant="outlined">
                          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" color="text.secondary">
                              In progress
                            </Typography>
                            <Typography variant="h5" color="warning.main">
                              {rollupOverall.in_progress_count}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={6} sm={4} md={3}>
                        <Card variant="outlined">
                          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" color="text.secondary">
                              Unassigned OPEN
                            </Typography>
                            <Typography variant="h5">
                              {rollupOverall.unassigned_open_count}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                    {raidRollup && raidRollup.project_summary.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          By project
                        </Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Project</TableCell>
                                <TableCell align="right">Open</TableCell>
                                <TableCell align="right">Overdue</TableCell>
                                <TableCell align="right">In progress</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {raidRollup.project_summary.map((p) => (
                                <TableRow key={p.entity_id}>
                                  <TableCell>{p.entity_name}</TableCell>
                                  <TableCell align="right">{p.open_count}</TableCell>
                                  <TableCell align="right">
                                    <Typography
                                      color={p.overdue_count > 0 ? 'error' : 'text.secondary'}
                                    >
                                      {p.overdue_count}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">{p.in_progress_count}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}
                    {raidRollup && raidRollup.assignee_summary.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          By assignee (OPEN items)
                        </Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Assignee</TableCell>
                                <TableCell align="right">Open</TableCell>
                                <TableCell align="right">Overdue</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {raidRollup.assignee_summary.map((a) => (
                                <TableRow key={a.assignee}>
                                  <TableCell>{a.assignee}</TableCell>
                                  <TableCell align="right">{a.open_count}</TableCell>
                                  <TableCell align="right">
                                    <Typography
                                      color={a.overdue_count > 0 ? 'error' : 'text.secondary'}
                                    >
                                      {a.overdue_count}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}
                  </>
                )}
              </Paper>
            </Grid>
          )}

          {/* Summary Cards (direct children when parent selected) */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <WarningIcon color="error" fontSize="small" />
                      <Typography variant="subtitle2" color="text.secondary">
                        {showProjectRollup ? 'Open (direct children)' : 'Total Open RAIDs'}
                      </Typography>
                    </Stack>
                    <Typography variant="h4" color={totalOpenRAIDs > 0 ? 'error.main' : 'success.main'}>
                      {totalOpenRAIDs}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <WarningIcon color="warning" fontSize="small" />
                      <Typography variant="subtitle2" color="text.secondary">
                        In Progress
                      </Typography>
                    </Stack>
                    <Typography variant="h4" color="warning.main">
                      {totalInProgressRAIDs}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              {raidSummary.map((item) => (
                <Grid item xs={12} sm={6} md={3} key={item.raid_category}>
                  <Card variant="outlined">
                    <CardContent>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <Chip
                          label={item.raid_category}
                          size="small"
                          sx={{ backgroundColor: RAID_COLORS[item.raid_category] || '#9e9e9e', color: 'white' }}
                        />
                      </Stack>
                      <Typography variant="h5" color="text.primary">
                        {item.open_count}{' '}
                        <Typography component="span" variant="body2" color="text.secondary">
                          open
                        </Typography>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.in_progress_count} in progress • {item.total_count} total
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* RAID Bar Chart */}
          <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, height: 350 }}>
          {raidSummary.length > 0 ? (
            <ReactECharts
              option={getRAIDBarOption()}
              style={{ height: '100%' }}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography color="text.secondary">No RAID data available</Typography>
            </Box>
          )}
        </Paper>
      </Grid>

      {/* RAID Pie Chart */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, height: 350 }}>
          {raidSummary.length > 0 ? (
            <ReactECharts
              option={getRAIDPieOption()}
              style={{ height: '100%' }}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography color="text.secondary">No RAID data available</Typography>
            </Box>
          )}
        </Paper>
      </Grid>

      {/* RAID by Entity Type */}
      {Object.keys(raidByEntityType).length > 0 && (
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              RAIDs by Entity Type
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(raidByEntityType).map(([category, data]) => (
                <Grid item xs={12} sm={6} md={4} key={category}>
                  <Box sx={{ height: 200 }}>
                    <ReactECharts
                      option={getRAIDByEntityTypeOption(category, data)}
                      style={{ height: '100%' }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      )}

      {/* RAID by Severity */}
      {Object.keys(raidBySeverity).length > 0 && (
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              RAIDs by Severity
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(raidBySeverity).map(([category, data]) => (
                <Grid item xs={12} sm={6} md={4} key={category}>
                  <Box sx={{ height: 200 }}>
                    <ReactECharts
                      option={getRAIDBySeverityOption(category, data)}
                      style={{ height: '100%' }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      )}

      {/* RAID Details Table */}
      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            RAID Details
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Open</TableCell>
                  <TableCell align="right">In Progress</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {raidSummary.length > 0 ? (
                  raidSummary.map((item) => (
                    <TableRow key={item.raid_category}>
                      <TableCell>
                        <Chip
                          label={item.raid_category}
                          size="small"
                          sx={{ backgroundColor: RAID_COLORS[item.raid_category] || '#9e9e9e', color: 'white' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography color={item.open_count > 0 ? 'error.main' : 'text.secondary'}>
                          {item.open_count}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography color={item.in_progress_count > 0 ? 'warning.main' : 'text.secondary'}>
                          {item.in_progress_count}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{item.total_count}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography color="text.secondary">No RAID data available</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
        </Fragment>
      )}

      <Grid item xs={12}>
        <Paper sx={{ p: 2, mb: 1 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            OPEN RAID items (sorted: overdue first, then oldest by RAID creation)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Only items whose latest status is OPEN. The table loads one page at a time (default 10 rows) with full
            detail history fetched only for rows on that page. Summaries below reflect all OPEN items. Overdue uses
            the same date rules as review submit/close validation.
          </Typography>
          {openBreakdownLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {openBreakdownError && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {openBreakdownError}
            </Alert>
          )}
          {!openBreakdownLoading && !openBreakdownError && openBreakdown && (
            <Fragment>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Total OPEN: <strong>{openBreakdown.total_open}</strong>
                {' · '}
                Overdue among OPEN: <strong>{openBreakdown.total_overdue}</strong>
                {!breakdownFetchFull && openBreakdown.total_items > 0 && (
                  <>
                    {' · '}
                    Showing <strong>{openBreakdown.items.length}</strong> of{' '}
                    <strong>{openBreakdown.total_items}</strong> on this page
                  </>
                )}
              </Typography>

              {openBreakdown.dimension_summary.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      Summary by review dimension
                    </Typography>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={dimensionSummaryViz}
                      onChange={(_event: React.MouseEvent<HTMLElement>, next: 'pie' | 'bar' | null) => {
                        if (next !== null) setDimensionSummaryViz(next);
                      }}
                      aria-label="Dimension summary chart type"
                    >
                      <ToggleButton value="pie">Pie</ToggleButton>
                      <ToggleButton value="bar">Bar</ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                  <Paper variant="outlined" sx={{ p: 1, mb: 2, height: 320 }}>
                    <ReactECharts
                      option={
                        dimensionSummaryViz === 'pie'
                          ? getDimensionSummaryPieOption(openBreakdown.dimension_summary)
                          : getDimensionSummaryBarOption(openBreakdown.dimension_summary)
                      }
                      style={{ height: '100%', minHeight: 280 }}
                      notMerge
                      lazyUpdate
                    />
                  </Paper>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Table
                  </Typography>
                  <TableContainer sx={{ maxWidth: '100%' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Dimension</TableCell>
                          <TableCell align="right">Open</TableCell>
                          <TableCell align="right">Overdue</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {openBreakdown.dimension_summary.map((d) => (
                          <TableRow key={d.review_entity_dimension}>
                            <TableCell>{d.review_entity_dimension}</TableCell>
                            <TableCell align="right">{d.open_count}</TableCell>
                            <TableCell align="right">{d.overdue_count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {openBreakdown.group_by === 'project' && openBreakdown.project_summary.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                    Summary by project
                  </Typography>
                  <TableContainer sx={{ maxWidth: '100%' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Project</TableCell>
                          <TableCell align="right">Open</TableCell>
                          <TableCell align="right">Overdue</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {openBreakdown.project_summary.map((p) => (
                          <TableRow key={p.entity_id}>
                            <TableCell>{p.entity_name || `Project #${p.entity_id}`}</TableCell>
                            <TableCell align="right">{p.open_count}</TableCell>
                            <TableCell align="right">{p.overdue_count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap alignItems="center">
                {!breakdownFetchFull && openBreakdown.total_items > 0 && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setBreakdownFetchFull(true);
                      setBreakdownPage(0);
                    }}
                  >
                    Fetch all OPEN items (up to 5000)
                  </Button>
                )}
                {breakdownFetchFull && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setBreakdownFetchFull(false);
                      setBreakdownPage(0);
                    }}
                  >
                    Back to paginated view
                  </Button>
                )}
              </Stack>

              {openBreakdown.truncated && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Response is limited to the first 5000 OPEN items. Use filters or RAID exports for the full list.
                </Alert>
              )}

              {openBreakdown.total_items === 0 ? (
                <Typography color="text.secondary">No OPEN RAID items.</Typography>
              ) : (
                <>
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                    RAID list
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {isProgramAccountRollup && <TableCell>Project</TableCell>}
                          <TableCell>Dimension</TableCell>
                          <TableCell>Title</TableCell>
                          <TableCell>Category</TableCell>
                          <TableCell align="right">Days in OPEN</TableCell>
                          <TableCell align="center">Overdue</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {openBreakdown.items.map((row) => (
                          <TableRow key={row.raid_id}>
                            {isProgramAccountRollup && (
                              <TableCell>
                                {row.project_entity_name || `Project #${row.project_entity_id ?? ''}`}
                              </TableCell>
                            )}
                            <TableCell>{row.review_entity_dimension}</TableCell>
                            <TableCell>{row.title}</TableCell>
                            <TableCell>
                              <Chip
                                label={row.raid_category}
                                size="small"
                                sx={{
                                  backgroundColor: RAID_COLORS[row.raid_category] || '#9e9e9e',
                                  color: 'white',
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">{row.days_open}</TableCell>
                            <TableCell align="center">
                              {row.is_overdue ? (
                                <Chip size="small" color="error" label={row.overdue_field || 'Overdue'} />
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  —
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {!breakdownFetchFull && (
                    <TablePagination
                      component="div"
                      count={openBreakdown.total_items}
                      page={breakdownPage}
                      onPageChange={(_e, next) => setBreakdownPage(next)}
                      rowsPerPage={breakdownRowsPerPage}
                      rowsPerPageOptions={[...OPEN_RAID_PAGE_SIZE_OPTIONS]}
                      onRowsPerPageChange={(e) => {
                        setBreakdownRowsPerPage(parseInt(e.target.value, 10));
                        setBreakdownPage(0);
                      }}
                    />
                  )}
                </>
              )}
            </Fragment>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default RAIDsTab;
